'use memo';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import type { Person } from '@/components/Combobox/PersonSelectorCombobox';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import { WorkerSelect } from '@/components/Select/WorkerSelect';
import { Text } from '@/components/Typography/Text';
import {
  clearTargetSelectionData,
  getTargetSelectionData,
  updateTargetIds,
} from '@/features/operation-package/-components/SelectTargetFlow/-store/session-storage';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';

/**
 * セルフレポート Start New の対象者選択（モック）。
 * SelectTargetFlow の sessionStorage を利用する簡易画面。
 */
export const Route = createFileRoute(
  '/_staff/it-operation-core/_root/in-house-packages/$packageId/start-targets',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { packageId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.itOperationCore.inHousePackages.startTargets',
  });
  const tc = useCommonTranslation();

  const session = getTargetSelectionData();
  const [selectedPerson, setSelectedPerson] = useState<Person | undefined>(
    undefined,
  );

  const handleBack = useCallback(() => {
    clearTargetSelectionData();
    navigate({
      to: '/it-operation-core/in-house-packages/$packageId',
      params: { packageId },
    });
  }, [navigate, packageId]);

  const handleStart = useCallback(() => {
    if (!selectedPerson?.id) return;
    updateTargetIds([selectedPerson.id]);
    clearTargetSelectionData();
    navigate({
      to: '/it-operation-core/in-house-packages/$packageId',
      params: { packageId },
    });
  }, [navigate, packageId, selectedPerson]);

  return (
    <FillScreen className="p-6">
      <VStack className="mx-auto w-full max-w-lg gap-6 rounded-md border border-neutral-border bg-neutral-fill p-6">
        <Text variant="h3">{t('title')}</Text>
        <Text variant="p" className="text-neutral-muted-on-fill">
          {session?.packageTypeName ?? t('fallbackPatternName')}
        </Text>
        <WorkerSelect
          value={selectedPerson}
          onChange={setSelectedPerson}
          placeHolder={t('selectPerson')}
        />
        <HStack className="justify-end gap-2">
          <ActionButton variant="ghost" onClick={handleBack}>
            {tc('action.cancel')}
          </ActionButton>
          <ActionButton
            variant="default"
            onClick={handleStart}
            disabled={!selectedPerson?.id}
          >
            {t('start')}
          </ActionButton>
        </HStack>
      </VStack>
    </FillScreen>
  );
}
